import {SagaIterator} from 'redux-saga';
import {call, put, putResolve, select} from 'redux-saga/effects';

import {sendEventForPlaceSubscribe} from 'desktop/pages/checkout/components/placeOrder/utils/utils';
import {LOGIN_METHOD, STEPS} from 'desktop/pages/checkout/constants/checkout';
import {handleSubscriptionAnalytics} from 'desktop/pages/checkout/utils/newsletterSubscription/analytics';
import {CustomerType} from 'hostApplicationStoreType';
import {apiConfig} from 'libs/api/api';
import {localStore} from 'libs/storage/storage';
import {
    sendEventToDataLayerForCustomerData,
    setUserDataToDataLayer,
} from 'sagas/analytics/analytics';

import {setOptionShippingMethod} from '../../../reducers/analytics/analytics';
import {saveShippingMethod} from '../../../reducers/deliveryStep/shippingMethods/shippingMethods';
import {getOrderReviewTotals} from '../../../reducers/orderReviewTotals/orderReviewTotals';
import {startSetCheckoutStepSaga} from '../../../reducers/sagaStep/sagaStep';
import {changeStepStatus} from '../../../reducers/status/status';
import {getSaveAddressErrorMessage} from '../../../selectors/deliveryStep/deliveryStep';
import {getPayerInfo} from '../../../selectors/paymentStep/paymentStep';
import {getCustomerEmail} from '../../../selectors/shippingAddress/shippingAddress';
import {
    getSelectedShippingMethodCode,
    getShippingMethodsStatus,
} from '../../../selectors/shippingMethods/shippingMethods';
import {saveCustomerAddressSaga} from '../saveCustomerAddressSaga/saveCustomerAddressSaga';

/**
 * Save selected shipping method to backend
 * @param {string} shippingMethodCode - Code of selected shipping method
 */
export function* saveShippingMethodSaga(shippingMethodCode: string): SagaIterator {
    yield putResolve(saveShippingMethod(shippingMethodCode));
    const status = yield select(getShippingMethodsStatus);
    if (status === 'REJECTED') throw new Error('Error while saving shipping method.');
    yield putResolve(getOrderReviewTotals({collectTax: true, withTaxErrors: true}));
}

/**
 * Choose analytics by flag
 */
export function* handleAnalytics(message: string): SagaIterator {
    yield call(handleSubscriptionAnalytics, message);
}

/**
 * Send customer object on delivery step
 */
export function* sendCustomerObjectOnDeliveryStep(): SagaIterator {
    const isSubscribed = yield select(getPayerInfo);
    const email = yield select(getCustomerEmail);
    const {customer} = apiConfig.hostStore.getState();

    if (isSubscribed.options?.subscribe) {
        yield call(setUserDataToDataLayer, email, customer as CustomerType);
        const loginMethod = yield call([localStore, 'get'], LOGIN_METHOD);

        if (loginMethod === 'Apple') {
            yield call(
                sendEventForPlaceSubscribe,
                customer.email,
                `Create Account - ${loginMethod}`,
            );
        }
    } else {
        yield call(sendEventToDataLayerForCustomerData, '');
    }
}

/**
 * Switches to payment step
 */
export function* switchToPaymentStepSaga(): SagaIterator {
    const errorMessage = yield select(getSaveAddressErrorMessage);

    if (errorMessage) {
        yield call(handleAnalytics, `Error - ${errorMessage}`);
        return;
    }

    yield call(handleAnalytics, 'CTA - Continue');

    yield call(sendCustomerObjectOnDeliveryStep);
    yield put(startSetCheckoutStepSaga(STEPS.PAYMENT_STEP));
}

/**
 * Saves selected shipping method and updates totals information
 */
export function* saveDataFromDeliveryStepOrSwitchNextStep(): SagaIterator {
    const selectedShippingMethodCode = yield select(getSelectedShippingMethodCode);
    yield call(saveShippingMethodSaga, selectedShippingMethodCode);
}

/**
 * Validate and save addresses specified at delivery step and switches to next step
 */
export function* finishDeliveryStepSaga(): SagaIterator {
    try {
        yield call(saveCustomerAddressSaga);
        yield call(saveDataFromDeliveryStepOrSwitchNextStep);
        yield call(switchToPaymentStepSaga);
    } catch (error) {
        yield call([console, 'error'], error);
    }
}

/**
 * Finish delivery step initialization
 */
export function* finishDeliveryStepInitSaga(): SagaIterator {
    yield put(
        changeStepStatus({
            [STEPS.DELIVERY_STEP]: {
                loaded: false,
            },
        }),
    );
    yield call(finishDeliveryStepSaga);
    yield put(
        changeStepStatus({
            [STEPS.DELIVERY_STEP]: {
                loaded: true,
            },
        }),
    );
    yield put(setOptionShippingMethod());
}
